import {clip, getAllIndexes, hexToBase64} from "../utils";

export interface Image {
  id: string | null,
  type: PngImageType | JpegImageType | null,
  data: string,
}

interface RtfGroup {
  start: number,
  end: number,
  content: string
}

type PngImageType = 'image/png';
type JpegImageType = 'image/jpeg';
interface ImageTypes {
  'pngblip': PngImageType,
  'jpegblip': JpegImageType,
}

const rtf_img_types: ImageTypes = {
  'pngblip': 'image/png',
  'jpegblip': 'image/jpeg',
};

const getPreviousNonWhitespaceChar = (content: string, index: number): string => {
  return getNonWhitespaceChar(content, index, -1);
}

const getNextNonWhitespaceChar = (content: string, index: number): string => {
  return getNonWhitespaceChar(content, index, 1);
}

const getNonWhitespaceChar = (content: string, startIndex: number, direction: number): string => {
  var index = startIndex + direction,
    current = content[ index ],
    whiteSpaceRegex = /[\s]/;

  while ( current && whiteSpaceRegex.test( current ) ) {
    index = index + direction;
    current = content[ index ];
  }

  return current;
}

/**
 * Primitive RTF parser.
 * Iterates over RTF content and search for the last } in the group
 * by keeping track of how many elements are open using a stack-like method.
 */
const getGroups = (content: string, search: string): RtfGroup[] => {
  return getAllIndexes(content, search)
    .map(i => {
      let start = i, open = 0, current = content[i];

      do {
        const isValidGroupStart = current === '{' && getPreviousNonWhitespaceChar(content, i) !== '\\' &&
          getNextNonWhitespaceChar(content, i) === '\\';
        const isValidGroupEnd = current === '}' && getPreviousNonWhitespaceChar(content, i) !== '\\' &&
          open > 0;

        if (isValidGroupStart) {
          open++;
        } else if (isValidGroupEnd) {
          open--;
        }

        current = content[++i];
      } while (current && open > 0);

      return {
        start: start,
        end: i,
        content: content.substring(start, i)
      };
    });
};

const removeSuperfluous = (rtf: string): string => {
  let len = 0;
  rtf = rtf.replace(/(\r\n|\n|\r)/gm, "");
  for (const header of getGroups(rtf, '{\\header')) {
    rtf = clip(rtf, header.start - len, header.end - len);
    len += header.content.length;
  }

  len = 0;
  for (const footer of getGroups(rtf, '{\\footer')) {
    rtf = clip(rtf, footer.start - len, footer.end - len);
    len += footer.content.length;
  }

  return rtf;
}

/**
 * Extract all {\\pict} RTF groups.
 */
const getPictRtfs = (rtf: string): string[] => {
  rtf = removeSuperfluous(rtf);

  return getGroups(rtf, '{\\pict')
    .map(group => group.content)
    .filter(content => content);
}

const isSupportedImageType = (rtf: string): keyof ImageTypes|null => {
  const types = Object.keys(rtf_img_types).join("|");
  const regexp = new RegExp(`\\\\(${types})`, "g")
  const match = regexp.exec(rtf);

  return match ? match[1] as keyof ImageTypes : null;
}

const encodeName = (str: string): string => {
  let encoded = '';
  for (let i = 0; i < str.length; i++) {
    let char = str[i];
    if (/[^A-Za-z0-9]/.test(char)) {
      encoded += "_x" + char.charCodeAt(0).toString(16).padStart(4, '0') + "_";
    } else {
      encoded += char;
    }
  }

  return encoded;
}

const getName = (rtf: string): string|null => {
  const match = /{\\sp{\\sn\s+wzName}{\\sv\s+(.*?)}}/g.exec(rtf);
  if (!match) return null;

  const [, name] = match;

  return encodeName(name);
}

const getPictDataAsBase64 = (picts: string[]): Image[] => {
  let images: Array<Image | null> = picts.map(rtf => {
    const name = getName(rtf);
    const match = /([\sa-fA-F0-9]+|\\bin([^}]+))}$/g.exec(rtf);
    if (!match) return null;

    const [, hexData, binData] = match;
    const data = binData
      ? btoa(binData.replace(/\s/g, '').trim())
      : hexToBase64(hexData.replace(/\s/g, '').trim());

    const type = isSupportedImageType(rtf);
    if (type === null) return null;

    return { id: name, type: rtf_img_types[type], data };
  });

  return images.filter((image): image is Image => image !== null);
}

const getImages = (rtf_content: string): Image[] => {
  return getPictDataAsBase64(getPictRtfs(rtf_content));
}

export {getImages};
